/**
 * Cross-Tab Synchronization Utility
 * 
 * Uses BroadcastChannel API to sync state across browser tabs:
 * - Account switches: When user switches account in one tab, all tabs update
 * - Logout: When user logs out in one tab, all tabs log out
 * - Token refresh: Prevents multiple tabs from refreshing token simultaneously
 * 
 * Fallback: Uses localStorage events for browsers without BroadcastChannel support
 */

const CHANNEL_NAME = 'turnover-sync';

// Message types
export const SyncMessageType = {
  ACCOUNT_SWITCH: 'ACCOUNT_SWITCH',
  LOGOUT: 'LOGOUT',
  TOKEN_REFRESHED: 'TOKEN_REFRESHED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
};

class CrossTabSync {
  constructor() {
    this.channel = null;
    this.listeners = new Map();
    this.isSupported = typeof BroadcastChannel !== 'undefined';
    this.tabId = this.generateTabId();
    
    this.init();
  }

  generateTabId() {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  init() {
    if (this.isSupported) {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event) => this.handleMessage(event.data);
    } else {
      // Fallback: Use localStorage events for cross-tab communication
      window.addEventListener('storage', (event) => {
        if (event.key === CHANNEL_NAME && event.newValue) {
          try {
            const data = JSON.parse(event.newValue);
            this.handleMessage(data);
          } catch (e) {
            console.warn('Failed to parse cross-tab message:', e);
          }
        }
      });
    }
  }

  handleMessage(data) {
    // Ignore messages from this tab
    if (data.sourceTabId === this.tabId) {
      return;
    }

    const handlers = this.listeners.get(data.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data.payload);
        } catch (e) {
          console.error(`Error in cross-tab handler for ${data.type}:`, e);
        }
      });
    }
  }

  /**
   * Broadcast a message to all other tabs
   */
  broadcast(type, payload = {}) {
    const message = {
      type,
      payload,
      sourceTabId: this.tabId,
      timestamp: Date.now(),
    };

    if (this.isSupported && this.channel) {
      this.channel.postMessage(message);
    } else {
      // Fallback: Use localStorage
      localStorage.setItem(CHANNEL_NAME, JSON.stringify(message));
      // Clear immediately to allow same message to be sent again
      setTimeout(() => localStorage.removeItem(CHANNEL_NAME), 100);
    }
  }

  /**
   * Subscribe to a message type
   * @returns Unsubscribe function
   */
  subscribe(type, handler) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.listeners.get(type);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }

  /**
   * Broadcast account switch to other tabs
   */
  broadcastAccountSwitch(accountId, accountName) {
    this.broadcast(SyncMessageType.ACCOUNT_SWITCH, { accountId, accountName });
  }

  /**
   * Broadcast logout to other tabs
   */
  broadcastLogout() {
    this.broadcast(SyncMessageType.LOGOUT);
  }

  /**
   * Broadcast that token was refreshed (prevents duplicate refreshes)
   */
  broadcastTokenRefresh(_newToken) {
    this.broadcast(SyncMessageType.TOKEN_REFRESHED, { timestamp: Date.now() });
  }

  /**
   * Broadcast session expiration
   */
  broadcastSessionExpired() {
    this.broadcast(SyncMessageType.SESSION_EXPIRED);
  }

  /**
   * Subscribe to account switches
   */
  onAccountSwitch(handler) {
    return this.subscribe(SyncMessageType.ACCOUNT_SWITCH, handler);
  }

  /**
   * Subscribe to logout events
   */
  onLogout(handler) {
    return this.subscribe(SyncMessageType.LOGOUT, handler);
  }

  /**
   * Subscribe to token refresh events
   */
  onTokenRefresh(handler) {
    return this.subscribe(SyncMessageType.TOKEN_REFRESHED, handler);
  }

  /**
   * Subscribe to session expiration
   */
  onSessionExpired(handler) {
    return this.subscribe(SyncMessageType.SESSION_EXPIRED, handler);
  }

  /**
   * Cleanup - call when component unmounts or app closes
   */
  destroy() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }
}

// Singleton instance
const crossTabSync = new CrossTabSync();

export default crossTabSync;
